import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Target, Lightbulb, Users, CheckCircle2 } from 'lucide-react';
import { CTASection } from '../components/CTASection';
export function About() {
  const values = [{
    title: 'Reliability',
    description: 'We deliver on our promises, ensuring your critical operations never stop.',
    icon: Shield
  }, {
    title: 'Innovation',
    description: 'Investing in the latest, most efficient and sustainable equipment.',
    icon: Lightbulb
  }, {
    title: 'Safety',
    description: 'Uncompromising commitment to the health and safety of our team and clients.',
    icon: Target
  }, {
    title: 'Customer Focus',
    description: 'Building long-term partnerships through exceptional service delivery.',
    icon: Users
  }];
  return <main className="flex-grow" data-id="element-208">
      {/* Hero Banner */}
      <div className="relative py-24 bg-brand-dark overflow-hidden" data-id="element-209">
        <div className="absolute inset-0" data-id="element-210">
          <img src="https://rapidenergy.co.uk/wp-content/uploads/2026/03/Rapid-Energy-Fleet.webp" alt="Rapid Energy Fleet" className="w-full h-full object-cover opacity-20" data-id="element-211" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark to-brand-dark/50" data-id="element-212"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-213">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-heading font-extrabold text-white mb-6" data-id="element-214">
            About <span className="text-brand-green" data-id="element-215">Us</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-216">
            Your Critical Asset Hire Partner
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-217">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-218">
          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-219">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:w-1/2" data-id="element-220">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-dark mb-6" data-id="element-221">
                Our Story
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-222">
                <p data-id="element-223">
                  Established in February 2020, Rapid Energy is a forward
                  thinking, ambitious business operating in the UK temporary
                  heating and cooling sector.
                </p>
                <p data-id="element-224">
                  We produce and provide high end, efficient temporary heating
                  and cooling solutions to commercial, industrial, and public
                  sector clients. Our team of experienced engineers and project
                  managers work tirelessly to ensure your critical operations
                  remain uninterrupted.
                </p>
                <p data-id="element-225">
                  Despite being a relatively young company, our leadership team
                  brings decades of combined experience in the HVAC hire
                  industry, allowing us to deliver unparalleled service and
                  technical expertise.
                </p>
              </div>

              <div className="mt-8 space-y-4" data-id="element-226">
                {['Nationwide coverage 24/7/365', 'Modern, highly efficient fleet', 'Turnkey project management', 'In-house manufacturing capabilities'].map((item, i) => <div key={i} className="flex items-center text-brand-dark font-medium" data-id="element-227">
                    <CheckCircle2 className="w-5 h-5 text-brand-green mr-3" data-id="element-228" />
                    {item}
                  </div>)}
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="lg:w-1/2 relative" data-id="element-229">
              <div className="absolute -inset-4 bg-brand-green/20 rounded-3xl transform rotate-3" data-id="element-230"></div>
              <img src="https://rapidenergy.co.uk/wp-content/uploads/2026/03/Rapid-Energy-van-next-to-packaged-boiler.webp" alt="Rapid Energy Van and Packaged Boiler" className="relative rounded-2xl shadow-2xl w-full object-cover h-[500px]" data-id="element-231" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-brand-light" data-id="element-232">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-233">
          <div className="text-center mb-16" data-id="element-234">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-dark" data-id="element-235">
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-236">
            {values.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 text-center" data-id="element-237">
                <div className="w-16 h-16 bg-brand-dark rounded-full flex items-center justify-center mx-auto mb-6 text-brand-green" data-id="element-238">
                  <value.icon className="w-8 h-8" data-id="element-239" />
                </div>
                <h3 className="text-xl font-heading font-bold text-brand-dark mb-3" data-id="element-240">
                  {value.title}
                </h3>
                <p className="text-gray-600" data-id="element-241">{value.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <CTASection data-id="element-242" />
    </main>;
}